/**
 * Edit Plan Tool - Schema Definitions
 * 
 * Zod schemas for editing presentation plans with various operations.
 */

import { z } from "zod";
import { SectionSchema, SlideSchema, PlanSchema, type Plan, type Section, type Slide } from "../types";

// ═══════════════════════════════════════════════════════════════════════════
// OPERATION SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════

const BaseOperationSchema = z.object({
    type: z.enum([
        'add_section', 'remove_section', 'update_section', 'move_section',
        'add_slide', 'remove_slide', 'update_slide', 'move_slide',
        'reorder_sections', 'reorder_slides'
    ]).describe('Type of edit operation to perform')
});

export const AddSectionOperationSchema = BaseOperationSchema.extend({
    type: z.literal('add_section'),
    section: SectionSchema.describe('Section to add'),
    afterSectionId: z.string().optional().describe('ID of section after which to insert. If omitted, adds at end.')
});

export const RemoveSectionOperationSchema = BaseOperationSchema.extend({
    type: z.literal('remove_section'),
    sectionId: z.string().describe('ID of section to remove')
});

export const UpdateSectionOperationSchema = BaseOperationSchema.extend({
    type: z.literal('update_section'),
    sectionId: z.string().describe('ID of section to update'),
    updates: z.object({
        title: z.string().optional(),
        purpose: z.string().optional()
    }).describe('Properties to update')
});

export const MoveSectionOperationSchema = BaseOperationSchema.extend({
    type: z.literal('move_section'),
    sectionId: z.string().describe('ID of section to move'),
    afterSectionId: z.string().optional().describe('ID of section after which to move. If omitted, moves to start.')
});

export const AddSlideOperationSchema = BaseOperationSchema.extend({
    type: z.literal('add_slide'),
    sectionId: z.string().describe('ID of section to add slide to'),
    slide: SlideSchema.describe('Slide to add'),
    afterSlideId: z.string().optional().describe('ID of slide after which to insert. If omitted, adds at end of section.')
});

export const RemoveSlideOperationSchema = BaseOperationSchema.extend({
    type: z.literal('remove_slide'),
    sectionId: z.string().describe('ID of section containing the slide'),
    slideId: z.string().describe('ID of slide to remove')
});

export const UpdateSlideOperationSchema = BaseOperationSchema.extend({
    type: z.literal('update_slide'),
    sectionId: z.string().describe('ID of section containing the slide'),
    slideId: z.string().describe('ID of slide to update'),
    updates: z.object({
        title: z.string().optional(),
        purpose: z.string().optional()
    }).describe('Properties to update')
});

export const MoveSlideOperationSchema = BaseOperationSchema.extend({
    type: z.literal('move_slide'),
    slideId: z.string().describe('ID of slide to move'),
    fromSectionId: z.string().describe('ID of current section'),
    toSectionId: z.string().describe('ID of destination section'),
    afterSlideId: z.string().optional().describe('ID of slide after which to insert. If omitted, adds at end.')
});

export const ReorderSectionsOperationSchema = BaseOperationSchema.extend({
    type: z.literal('reorder_sections'),
    sectionIds: z.array(z.string()).describe('Array of section IDs in desired order')
});

export const ReorderSlidesOperationSchema = BaseOperationSchema.extend({
    type: z.literal('reorder_slides'),
    sectionId: z.string().describe('ID of section whose slides to reorder'),
    slideIds: z.array(z.string()).describe('Array of slide IDs in desired order')
});

/**
 * Discriminated union of all operation types
 */
export const OperationSchema = z.discriminatedUnion('type', [
    AddSectionOperationSchema,
    RemoveSectionOperationSchema,
    UpdateSectionOperationSchema,
    MoveSectionOperationSchema,
    AddSlideOperationSchema,
    RemoveSlideOperationSchema,
    UpdateSlideOperationSchema,
    MoveSlideOperationSchema,
    ReorderSectionsOperationSchema,
    ReorderSlidesOperationSchema
]);

// ═══════════════════════════════════════════════════════════════════════════
// INPUT SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Parameters for editing an existing plan
 */
export const EditPlanParamsSchema = z.object({
    operations: z.array(OperationSchema)
        .min(1, 'At least one operation must be specified')
        .describe('List of edit operations to apply to the plan'),
    currentPlan: PlanSchema.describe('The current complete plan to modify')
});

// ═══════════════════════════════════════════════════════════════════════════
// OUTPUT TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Response from editing a plan
 */
export interface EditPlanResponse {
    success: boolean;
    plan: Plan;
    changes: {
        added: string[];      // IDs of added sections/slides
        removed: string[];    // IDs of removed sections/slides
        modified: string[];   // IDs of modified sections/slides
        reordered: string[];  // IDs of reordered sections/slides
    };
    message: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export type Operation = z.infer<typeof OperationSchema>;
export type EditPlanParams = z.infer<typeof EditPlanParamsSchema>;

// Re-export types used in operations
export type { Plan, Section, Slide };

